/**
 * =============================================================================
 * ASL Multi-Tenant Partner Shop - Content-Blocks Client (Issue #1, Paket A)
 * =============================================================================
 * Liest und schreibt CMS-artige Inhaltsblöcke pro Mandant und Seite:
 *   GET  /api/content?tenant={slug}&page={page}
 *        -> { ok, tenant: {id, slug, name}, page, blocks: [{block_key, type: 'text'|'image', value, image_url, sort}] }
 *   PUT  /api/admin/content   Body: { tenant_id, page, blocks: [{block_key, type, value?, image_url?, sort?}] }
 *   DELETE /api/admin/content?tenant_id={id}&page={page}&block_key={key}  (Reset auf Standardwert)
 *   POST /api/admin/content/image  (multipart FormData) -> { ok, image_url }
 *
 * Fallback-Prinzip: Fehlt ein Block oder schlägt der Fetch fehl, greifen die
 * bisher hartkodierten Shop-Texte (CONTENT_FALLBACKS). Es gibt keinen
 * Loader, der das Rendern blockiert, und keinen Crash.
 */

import { useEffect, useState } from 'react';
import { ApiError, apiFetch, getAdminApiKey } from './api.js';

import {
  CONTENT_PAGE_KEYS,
  SLOT_REGISTRY,
  REGISTERED_SLOTS,
  CONTENT_FALLBACKS,
  SLOT_METADATA,
  getSlotDefinition,
  getDefaultSlotValue,
  isValidSlot,
  getAllSlots,
  getSlotsByPage,
  getSlotsByFile,
  resolveSlotText,
  getSlotStats
} from './slot-registry.js';

export {
  CONTENT_PAGE_KEYS,
  SLOT_REGISTRY,
  REGISTERED_SLOTS,
  CONTENT_FALLBACKS,
  SLOT_METADATA,
  getSlotDefinition,
  getDefaultSlotValue,
  isValidSlot,
  getAllSlots,
  getSlotsByPage,
  getSlotsByFile,
  resolveSlotText,
  getSlotStats
};

// -----------------------------------------------------------------------------
// Pure Helfer (block-freundlich getestet)
// -----------------------------------------------------------------------------
export function normalizeContentBlocks(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((b) => b && typeof b === 'object' && typeof b.block_key === 'string' && b.block_key.trim())
    .map((b, index) => ({
      block_key: b.block_key,
      type: b.type === 'image' ? 'image' : 'text',
      value: typeof b.value === 'string' ? b.value : '',
      image_url: typeof b.image_url === 'string' ? b.image_url : '',
      sort: Number.isFinite(b.sort) ? b.sort : index,
    }));
}

/**
 * Flache Map block_key -> Anzeigewert (Text bzw. Bild-URL).
 * Leere Blöcke werden weggelassen, damit der Fallback greift.
 */
export function blocksToMap(blocks) {
  const map = {};
  for (const block of normalizeContentBlocks(blocks)) {
    if (block.type === 'image') {
      if (block.image_url) map[block.block_key] = block.image_url;
    } else if (typeof block.value === 'string' && block.value.trim()) {
      map[block.block_key] = block.value;
    }
  }
  return map;
}

/**
 * Liest einen Wert aus der Block-Map und fällt bei Fehlen/Leere auf den
 * übergebenen Fallback (der heutige hartkodierte Text) zurück.
 */
export function resolveContentValue(map, key, fallback) {
  if (!map || typeof map !== 'object') return fallback;
  const value = map[key];
  return typeof value === 'string' && value.trim() ? value : fallback;
}

/**
 * Editor-Entwurf aus API-Blöcken: { [block_key]: { type, value, image_url } }.
 * Fehlende Blöcke werden als leere Felder angelegt (Platzhalter = Fallback-Text).
 */
export function toContentDraft(fields, blocks) {
  const draft = {};
  const source = {};
  for (const block of normalizeContentBlocks(blocks)) {
    source[block.block_key] = block;
  }
  for (const field of Array.isArray(fields) ? fields : []) {
    if (!field || typeof field.key !== 'string') continue;
    const src = source[field.key];
    draft[field.key] = {
      type: field.type === 'image' ? 'image' : 'text',
      value: src && typeof src.value === 'string' ? src.value : '',
      image_url: src && typeof src.image_url === 'string' ? src.image_url : '',
    };
  }
  return draft;
}

/**
 * PUT-Payload-Blöcke aus dem Editor-Entwurf. Leere Felder werden ausgelassen,
 * damit im Shop weiterhin der Fallback-Text greift. sort = Feldreihenfolge.
 */
export function fromContentDraft(fields, draft) {
  const blocks = [];
  (Array.isArray(fields) ? fields : []).forEach((field, index) => {
    if (!field || typeof field.key !== 'string') return;
    const entry = draft && typeof draft === 'object' ? draft[field.key] : null;
    if (!entry) return;
    if (field.type === 'image') {
      if (typeof entry.image_url === 'string' && entry.image_url.trim()) {
        blocks.push({
          block_key: field.key,
          type: 'image',
          image_url: entry.image_url.trim(),
          sort: index,
        });
      }
    } else if (typeof entry.value === 'string' && entry.value.trim()) {
      blocks.push({
        block_key: field.key,
        type: 'text',
        value: entry.value.trim(),
        sort: index,
      });
    }
  });
  return blocks;
}

// -----------------------------------------------------------------------------
// API-Calls
// -----------------------------------------------------------------------------

/**
 * Liest die Inhaltsblöcke einer Seite für einen Mandanten-Slug.
 * @returns {Promise<{ok: boolean, tenant: object|null, page: string, blocks: Array}>}
 */
export async function fetchContent(tenantSlug, page) {
  if (!tenantSlug) {
    throw new Error('Kein Mandanten-Slug für den Content-Abruf angegeben.');
  }
  const params = new URLSearchParams({ tenant: tenantSlug, page: page || 'home' });
  const res = await apiFetch(`/api/content?${params.toString()}`);
  return {
    ok: Boolean(res?.ok),
    tenant: res?.tenant && typeof res.tenant === 'object' ? res.tenant : null,
    page: res?.page || page || 'home',
    blocks: normalizeContentBlocks(res?.blocks),
  };
}

/**
 * Speichert die Inhaltsblöcke einer Seite (Admin, CSRF-Header via apiFetch).
 * @param {string} tenantId
 * @param {string} page
 * @param {Array<{block_key, type, value?, image_url?, sort?}>} blocks
 * @param {string|null} [apiKey]
 */
export async function saveContent(tenantId, page, blocks, apiKey = getAdminApiKey()) {
  if (!tenantId) {
    throw new Error('Kein Mandant ausgewählt. Speichern nicht möglich.');
  }
  const payload = {
    tenant_id: tenantId,
    page: page || 'home',
    blocks: normalizeContentBlocks(blocks).map((block) => {
      const out = { block_key: block.block_key, type: block.type, sort: block.sort };
      if (block.type === 'image') {
        if (block.image_url) out.image_url = block.image_url;
      } else if (block.value) {
        out.value = block.value;
      }
      return out;
    }),
  };
  return apiFetch('/api/admin/content', {
    method: 'PUT',
    token: apiKey || undefined,
    body: payload,
  });
}

/**
 * Inline-Speichern für den Storefront-Editor (Issue #3, Paket A).
 * Sendet GENAU EINEN Block 1:1 — auch mit LEEREM value (leerer Block = Reset
 * auf den Standardtext, Server akzeptiert '' als String). saveContent() würde
 * leere Werte herausfiltern und den Reset dadurch verhindern.
 * @param {string} tenantId
 * @param {string} page
 * @param {{block_key: string, type: 'text'|'image', value?: string, image_url?: string, sort?: number}} block
 * @param {string|null} [apiKey] — null = Session-Cookie (tenant_admin)
 */
export async function saveInlineBlock(tenantId, page, block, apiKey = null) {
  if (!tenantId) {
    throw new Error('Kein Mandant ausgewählt. Speichern nicht möglich.');
  }
  const clean = {
    block_key: block.block_key,
    type: block.type === 'image' ? 'image' : 'text',
    sort: Number.isFinite(block.sort) ? block.sort : 0,
  };
  if (clean.type === 'text') {
    clean.value = typeof block.value === 'string' ? block.value : '';
  } else if (block.image_url) {
    clean.image_url = block.image_url;
  }
  return apiFetch('/api/admin/content', {
    method: 'PUT',
    token: apiKey || undefined,
    body: { tenant_id: tenantId, page: page || 'home', blocks: [clean] },
  });
}

/**
 * Setzt einen einzelnen Slot auf den Standardwert zurück (DELETE /api/admin/content).
 * @param {string} tenantId
 * @param {string} page
 * @param {string} blockKey
 * @param {string|null} [apiKey]
 */
export async function resetSlotContent(tenantId, page, blockKey, apiKey = null) {
  if (!tenantId || !page || !blockKey) {
    throw new Error('Mandant, Seite und Slot-ID sind erforderlich.');
  }
  const params = new URLSearchParams({ tenant_id: tenantId, page, block_key: blockKey });
  return apiFetch(`/api/admin/content?${params.toString()}`, {
    method: 'DELETE',
    token: apiKey || undefined,
  });
}

/**
 * Setzt alle Slots einer Seite auf Standardwerte zurück.
 * @param {string} tenantId
 * @param {string} page
 * @param {string|null} [apiKey]
 */
export async function resetPageContent(tenantId, page, apiKey = null) {
  if (!tenantId || !page) {
    throw new Error('Mandant und Seite sind erforderlich.');
  }
  const params = new URLSearchParams({ tenant_id: tenantId, page });
  return apiFetch(`/api/admin/content?${params.toString()}`, {
    method: 'DELETE',
    token: apiKey || undefined,
  });
}

/**
 * Setzt alle Slots aller Seiten für einen Mandanten auf Standardwerte zurück.
 * @param {string} tenantId
 * @param {string|null} [apiKey]
 */
export async function resetAllContent(tenantId, apiKey = null) {
  if (!tenantId) {
    throw new Error('Mandant ist erforderlich.');
  }
  const params = new URLSearchParams({ tenant_id: tenantId, all: 'true' });
  return apiFetch(`/api/admin/content?${params.toString()}`, {
    method: 'DELETE',
    token: apiKey || undefined,
  });
}

/**
 * Lädt ein Bild nach /api/admin/content/image hoch (multipart).
 * apiFetch wird absichtlich NICHT verwendet, da FormData nicht als JSON
 * serialisiert werden darf.
 * @returns {Promise<{ok: boolean, image_url: string, data: object}>}
 */
export async function uploadContentImage(file, apiKey = getAdminApiKey()) {
  const isFileLike = typeof File !== 'undefined' ? file instanceof File : false;
  if (!isFileLike || !file) {
    throw new Error('Bitte wählen Sie eine gültige Bilddatei aus.');
  }
  const form = new FormData();
  form.append('image', file, file.name || 'upload');

  const headers = { Accept: 'application/json', 'X-ASL-Admin-CSRF': '1' };
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;

  let response;
  try {
    response = await fetch('/api/admin/content/image', {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: form,
    });
  } catch (err) {
    throw new ApiError(`Netzwerkfehler beim Bild-Upload: ${err?.message || err}`, 0);
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(data?.error || data?.message || `Bild-Upload fehlgeschlagen (HTTP ${response.status})`, response.status, data);
  }
  const imageUrl = data?.image_url || data?.url || data?.path || '';
  if (!imageUrl) {
    throw new Error('Der Server hat keine Bild-URL zurückgegeben.');
  }
  return { ok: true, image_url: imageUrl, data };
}

// -----------------------------------------------------------------------------
// Fehlerformatierung (Deutsche verständliche Meldungen)
// -----------------------------------------------------------------------------

/**
 * Übersetzt Server-Fehlermeldungen und Fehler-Codes in verständliche deutsche Texte.
 * Deckt alle nutzerseitigen 4xx-Statuscodes und Fehlertypen ab (D1).
 * @param {any} err
 * @returns {string}
 */
export function formatContentErrorMessage(err) {
  if (!err) return 'Ein unbekannter Fehler ist aufgetreten.';
  if (typeof err === 'string') return err;

  const rawMsg = err.message || '';
  const data = err?.data || err?.details;
  const code = err?.code || data?.code;
  const status = typeof err?.status === 'number' ? err.status : (typeof err?.statusCode === 'number' ? err.statusCode : null);

  if (data && Array.isArray(data.errors) && data.errors.length > 0) {
    const formatted = data.errors.map((e) => {
      if (typeof e !== 'string') return String(e);
      if (e.includes('kein registrierter Slot')) return 'Unregistrierter Slot abgewiesen';
      if (e.includes('nicht erlaubt für Text-Slot') || e.includes('erlaubt sind text, image')) return 'Ungültiger Slot-Typ';
      if (e.includes('maximal 10000 Zeichen') || e.includes('maximal 10.000 Zeichen')) return 'Text zu lang (max. 10.000 Zeichen)';
      if (e.includes('image_url')) return 'Ungültige Bild-Adresse';
      return e;
    });
    return `Speichern fehlgeschlagen: ${formatted.join('; ')}`;
  }

  // Spezifische Fehlercodes (400 / Business Logic)
  if (code === 'UNKNOWN_SLOT' || rawMsg.includes('kein registrierter Slot') || rawMsg.includes('Unbekannter Slot')) {
    return 'Dieser Slot ist im System nicht registriert und kann nicht gespeichert werden.';
  }
  if (code === 'INVALID_TYPE' || rawMsg.includes('nicht erlaubt für Text-Slot') || rawMsg.includes('erlaubt sind text, image') || rawMsg.includes('erwartet für Bild-Slot')) {
    return 'Der angegebene Typ (Text/Bild) ist für diesen Slot nicht zulässig.';
  }
  if (code === 'VALUE_TOO_LONG' || rawMsg.includes('maximal 10000 Zeichen') || rawMsg.includes('maximal 10.000 Zeichen')) {
    return 'Der eingegebene Text ist zu lang (maximal 10.000 Zeichen erlaubt).';
  }
  if (code === 'NAME_TOO_LONG' || rawMsg.includes('maximal 80 Zeichen')) {
    return 'Der Produktname darf höchstens 80 Zeichen lang sein.';
  }
  if (code === 'DESCRIPTION_TOO_LONG' || rawMsg.includes('maximal 5.000 Zeichen') || rawMsg.includes('maximal 5000 Zeichen')) {
    return 'Die Beschreibung darf höchstens 5.000 Zeichen lang sein.';
  }
  if (code === 'HTML_NOT_ALLOWED' || rawMsg.includes('HTML')) {
    return 'Bitte keinen HTML-Code eingeben (Zeichen wie <b> sind nicht erlaubt).';
  }
  if (code === 'INVALID_IMAGE_URL' || rawMsg.includes('nur https://… oder relativer Pfad') || rawMsg.includes('kein javascript:/data:')) {
    return 'Bitte geben Sie eine gültige Bild-Adresse (https://…) oder einen relativen Pfad (/…) an.';
  }
  if (code === 'MISSING_TENANT_ID' || rawMsg.includes('tenant_id')) {
    return 'Kein gültiger Mandant ausgewählt.';
  }
  if (code === 'TENANT_NOT_FOUND' || rawMsg.includes('Tenant nicht gefunden') || rawMsg.includes('Mandant nicht gefunden')) {
    return 'Der angeforderte Mandant wurde nicht gefunden.';
  }
  if (code === 'PRODUCT_NOT_FOUND' || rawMsg.includes('Produkt nicht gefunden')) {
    return 'Das angeforderte Produkt wurde nicht gefunden.';
  }
  if (code === 'INVALID_PAGE' || rawMsg.includes('Unbekannte Seite')) {
    return 'Die angeforderte Inhaltsseite ist ungültig.';
  }
  if (code === 'PAYLOAD_TOO_LARGE' || rawMsg.includes('maximal 100 Blöcke')) {
    return 'Zu viele Blöcke auf einmal übertragen.';
  }
  if (code === 'INVALID_JSON' || rawMsg.includes('Ungültiges JSON')) {
    return 'Ungültige Eingabedaten übermittelt.';
  }
  if (code === 'NO_FIELDS' || rawMsg.includes('Mindestens ein Feld')) {
    return 'Es wurden keine Änderungen zum Speichern übergeben.';
  }
  if (code === 'UNKNOWN_FIELD' || rawMsg.includes('Unbekanntes Feld')) {
    return 'Ein ungültiges Feld wurde übermittelt.';
  }

  // HTTP-Statuscodes (4xx)
  if (status === 401 || code === 'UNAUTHORIZED' || rawMsg.includes('Sitzung ist abgelaufen') || rawMsg.includes('Unauthorized')) {
    return 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.';
  }
  if (status === 403 || code === 'FORBIDDEN' || rawMsg.includes('Berechtigung') || rawMsg.includes('scope')) {
    return 'Keine Berechtigung zum Bearbeiten der Inhalte dieses Mandanten.';
  }
  if (status === 404 || code === 'NOT_FOUND') {
    return 'Der angeforderte Datensatz wurde nicht gefunden.';
  }
  if (status === 405 || code === 'METHOD_NOT_ALLOWED') {
    return 'Diese Aktion wird vom Server nicht unterstützt.';
  }
  if (status === 408) {
    return 'Die Anfrage hat zu lange gedauert. Bitte erneut versuchen.';
  }
  if (status === 409 || code === 'CONFLICT') {
    return 'Konflikt bei der Datenverarbeitung. Bitte die Seite neu laden.';
  }
  if (status === 413) {
    return 'Die Datei oder Eingabe ist zu groß.';
  }
  if (status === 415) {
    return 'Dieses Datenformat wird nicht unterstützt.';
  }
  if (status === 422 || code === 'UNPROCESSABLE_ENTITY') {
    return 'Die Eingabedaten konnten nicht verarbeitet werden. Bitte prüfen Sie Ihre Angaben.';
  }
  if (status === 429 || code === 'RATE_LIMITED') {
    return 'Zu viele Anfragen in kurzer Zeit. Bitte warten Sie einen Moment.';
  }
  if (status === 400) {
    return 'Ungültige Eingabe. Bitte prüfen Sie Ihre Angaben.';
  }
  if (status !== null && status >= 500) {
    return 'Der Server konnte die Anfrage nicht verarbeiten. Bitte später erneut versuchen.';
  }
  if (status === 0 || rawMsg.toLowerCase().includes('network connection error')) {
    return 'Keine Verbindung zum Server. Bitte Verbindung prüfen.';
  }

  return rawMsg || 'Speichern fehlgeschlagen.';
}

// -----------------------------------------------------------------------------
// React-Hook (nicht blockierend)
// -----------------------------------------------------------------------------

/**
 * Lädt Content-Blöcke einer Seite im Hintergrund. Solange nichts geladen ist
 * (oder der Fetch fehlschlägt) bleibt map leer -> Aufrufer nutzt Fallback-Texte.
 * `revision` (EditModeContext) triggert bei Erhöhung einen sofortigen Refetch,
 * damit Inline-Edits ohne Reload sichtbar werden (Issue #3, Paket A).
 * @returns {{ blocks: Array|null, map: object, loading: boolean }}
 */
export function useContentBlocks(page, tenantSlug, { enabled = true, revision = 0 } = {}) {
  const [blocks, setBlocks] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !tenantSlug || !CONTENT_PAGE_KEYS.includes(page)) {
      return undefined;
    }
    let isMounted = true;
    setLoading(true);
    fetchContent(tenantSlug, page)
      .then((res) => {
        if (isMounted) setBlocks(res.blocks);
      })
      .catch(() => {
        // Fallback: keine Blöcke -> hartkodierte Texte bleiben sichtbar
        if (isMounted) setBlocks(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [page, tenantSlug, enabled, revision]);

  return { blocks, map: blocksToMap(blocks), loading };
}
